import React, { useCallback } from 'react';

import CartItem from './CartItem';
import { ICartItem } from '../../../CartProvider';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';

type Variant = 'default' | 'unassigned' | 'assigned' | 'deleted';

interface CartItemListProps {
  items: ICartItem[];
  onEdit?: (id: ICartItem['id']) => void;
  onDelete?: (item: ICartItem) => void;
  onQuantityChange?: (item: ICartItem, newQuantity: number) => void;
  onUndoDelete?: (item: ICartItem) => void;
  className?: string;
  disable?: {
    onEdit?: (item: ICartItem) => void;
    // onDelete?: (item: ICartItem) => void;
    // onQuantityChange?: (item: ICartItem) => void;
  };
  variant?: Variant | ((item: ICartItem) => Variant);
}
const CartItemList: React.FC<CartItemListProps> = observer(
  ({
    items,
    onEdit,
    onDelete,
    onQuantityChange,
    onUndoDelete,
    className,
    disable,
    variant: _variant = 'default',
  }) => {
    const variant = useCallback(
      (item: ICartItem) => {
        if (typeof _variant === 'function') {
          return _variant(item);
        }
        return _variant;
      },
      [_variant],
    );

    const renderCartItem = (item: ICartItem, className?: string) => (
      <CartItem
        item={item}
        onEdit={
          onEdit && !disable?.onEdit?.(item) && variant(item) !== 'deleted'
            ? () => onEdit(item.id as string)
            : undefined
        }
        onDelete={
          onDelete && variant(item) !== 'deleted'
            ? () => onDelete(item)
            : undefined
        }
        onQuantityChange={
          onQuantityChange && variant(item) !== 'deleted'
            ? (newQty: number) => onQuantityChange(item, newQty)
            : undefined
        }
        onUndoDelete={
          variant(item) === 'deleted' && onUndoDelete
            ? () => onUndoDelete(item)
            : undefined
        }
        className={className}
      />
    );

    return (
      <div className={clsx('flex flex-col gap-4', className)}>
        {items.map((item) => {
          if (variant(item) === 'default') {
            return <div key={item.id}>{renderCartItem(item)}</div>;
          }

          const style =
            variant(item) === 'assigned'
              ? {
                  background:
                    'linear-gradient(0deg, rgba(34, 34, 49, 0.12) 0%, rgba(34, 34, 49, 0.12) 100%), linear-gradient(93deg, rgba(126, 119, 179, 0.16) 2.92%, rgba(137, 137, 255, 0.16) 26.85%, rgba(108, 157, 255, 0.16) 50.68%, rgba(120, 207, 218, 0.16) 74.7%, rgba(159, 244, 169, 0.16) 98.63%)',
                }
              : undefined;

          return (
            <DashedGradientBorder
              key={item.id}
              radius={4}
              strokeWidth={1}
              dash={6}
              gap={6}
              stops={
                variant(item) === 'assigned'
                  ? [
                      { offset: 0, color: '#7E77B3' },
                      { offset: 0.25, color: '#8989FF' },
                      { offset: 0.5, color: '#6C9DFF' },
                      { offset: 0.75, color: '#78CFDA' },
                      { offset: 1, color: '#9FF4A9' },
                    ]
                  : [{ offset: 0, color: '#475569' }]
              }
              className="rounded"
              style={style}
            >
              {renderCartItem(
                item,
                variant(item) === 'assigned'
                  ? 'bg-transparent'
                  : variant(item) === 'deleted'
                    ? 'bg-[#47556914]'
                    : undefined,
              )}
            </DashedGradientBorder>
          );
        })}
      </div>
    );
  },
);

export default CartItemList;

type DashedGradientBorderProps = {
  radius?: number; // px
  strokeWidth?: number; // px
  dash?: number; // px
  gap?: number; // px
  stops?: { offset: number; color: string; opacity?: number }[];
  className?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
};

export function DashedGradientBorder({
  radius = 0,
  strokeWidth = 0,
  dash = 4,
  gap = 4,
  stops = [
    { offset: 0, color: '#fff' },
    { offset: 1, color: '#fff' },
  ],
  className,
  children,
  style,
}: DashedGradientBorderProps) {
  const id = React.useId();
  const ref = React.useRef<HTMLDivElement>(null);
  const [{ w, h }, setSize] = React.useState({ w: 0, h: 0 });

  React.useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver(([entry]) => {
      const cr = entry.contentRect;
      setSize({ w: Math.max(0, cr.width), h: Math.max(0, cr.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const half = strokeWidth / 2;
  const rx = Math.min(radius, (w - strokeWidth) / 2);
  const ry = Math.min(radius, (h - strokeWidth) / 2);

  return (
    <div ref={ref} className={clsx('relative', className)} style={style}>
      {children}
      {w > 0 && h > 0 && strokeWidth > 0 && (
        <svg
          className="pointer-events-none absolute inset-0"
          width={w}
          height={h}
          // no viewBox -> lengths are in px
          aria-hidden
        >
          <defs>
            <linearGradient
              id={`grad-${id}`}
              x1={0}
              y1={0}
              x2={w}
              y2={0}
              gradientUnits="userSpaceOnUse"
            >
              {stops.map((s, i) => (
                <stop
                  key={i}
                  offset={`${s.offset * 100}%`}
                  stopColor={s.color}
                  stopOpacity={s.opacity ?? 1}
                />
              ))}
            </linearGradient>
          </defs>

          <rect
            x={half}
            y={half}
            width={w - strokeWidth}
            height={h - strokeWidth}
            rx={rx}
            ry={ry}
            fill="none"
            stroke={`url(#grad-${id})`}
            strokeWidth={strokeWidth}
            strokeDasharray={`${dash} ${gap}`}
            strokeLinecap="round"
            strokeLinejoin="round"
            // thickness stays in px on zoom; dash lengths are already px
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
    </div>
  );
}
